import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

type Column = {
    column: number
    headerTitle: string
    component: React.ComponentType<Record<string, unknown>>
    style?: React.CSSProperties
}

type DynamicTableProps = {
    columns: Column[]
    data: unknown[]
    className?: string
    styledTable?: StyleSheet
    styledHeader?: StyleSheet
    styledRowHeader?: StyleSheet
    styledColumnHeader?: StyleSheet
    styledBody?: StyleSheet
    styledRowBody?: StyleSheet
    styledColumnBody?: StyleSheet
    footer: boolean
    footerType: string
    styledFooter?: StyleSheet
}

const DynamicTable: React.FC<DynamicTableProps> = ({
    columns,
    data,
    className,
    styledTable,
    styledHeader,
    styledRowHeader,
    styledColumnHeader,
    styledBody,
    styledRowBody,
    styledColumnBody,
    footer,
    footerType,
    styledFooter
}: DynamicTableProps): JSX.Element => {
    return (
        <TableGeneric className={className} style={styledTable}>
            <DivOverflowScrollbar>
                <THeadGeneric style={styledHeader}>
                    <TrGenericHeader style={styledRowHeader}>
                        {columns?.map((col, index) => (
                            <ThGeneric key={index} style={{ ...styledColumnHeader, ...col?.style }}>
                                {col.headerTitle}
                            </ThGeneric>
                        ))}
                    </TrGenericHeader>
                </THeadGeneric>
                <TbodyGeneric style={styledBody}>
                    {data?.map((row, rowIndex) => (
                        <TrGenericBody key={rowIndex} style={styledRowBody}>
                            {columns?.map((col, colIndex) => {
                                const CellComponent = col.component
                                const cellData = row[col.column]

                                return (
                                    <TdGeneric
                                        key={colIndex}
                                        style={{
                                            ...styledColumnBody,
                                            ...col?.style
                                        }}
                                    >
                                        <CellComponent data={cellData} />
                                    </TdGeneric>
                                )
                            })}
                        </TrGenericBody>
                    ))}
                </TbodyGeneric>
            </DivOverflowScrollbar>
            {footer && (
                <TfooterGeneric>
                    <TRFooterGeneric style={styledFooter}>
                        <TdFooterGeneric colSpan={columns.length} style={styledFooter}>
                            Showing <b>{`${data?.length ? data?.length : 0}`}</b> of{' '}
                            <b>{`${data?.length ? data?.length : 0}`}</b> {`${footerType}`}{' '}
                        </TdFooterGeneric>
                    </TRFooterGeneric>
                </TfooterGeneric>
            )}
        </TableGeneric>
    )
}

export default DynamicTable

const TableGeneric = styled.table`
    height: 100%;
    width: 100%;
    min-height: 250px;
    max-width: 100vw;
    border-collapse: collapse;
`
const THeadGeneric = styled.table`
    position: sticky;
    top: 0;
    z-index: 10;
`

const TdGeneric = styled.td`
    padding: 16px 0;
    // border-bottom: 1px solid var(--gray-300);
    display: table-cell;

    &:last-child {
        border-bottom: none;
    }
`

const TrGenericHeader = styled.tr`
    border-bottom: 1px solid var(--gray-300);
    display: table;
    width: 100%;
    table-layout: fixed;
`

const TrGenericBody = styled.tr`
    border-bottom: 1px solid var(--gray-300);
    display: table;
    width: 100%;
    table-layout: fixed;

    &:last-child {
        border-bottom: none;
    }
`

const TbodyGeneric = styled.tbody`
    display: block;
    height: ${({ footer }) => (footer ? '90%' : '86%')};
    position: relative;
`

const DivOverflowScrollbar = styled.div`
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;

    &::-webkit-scrollbar {
        width: 8px;
        border-radius: 5px;
    }

    &::-webkit-scrollbar-track {
        background-color: var(--gray-200);
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--gray-400);
        border-radius: 5px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: var(--gray-500);
    }
`

const ThGeneric = styled.th`
    padding: 8px 0;
    display: table-cell;
`
const TfooterGeneric = styled.tfoot`
    border-top: 1px solid var(--gray-300);
`

const TRFooterGeneric = styled.tr`
    td {
        font-family: 'SourceSansPro-SemiBold', sans-serif;
        color: var(--gray-700);
        padding: 0;
        b {
            font-family: 'SourceSansPro-SemiBold', sans-serif;
            color: var(--gray-800);
        }
    }
`
const TdFooterGeneric = styled.td`
    td {
        font-family: 'SourceSansPro-SemiBold', sans-serif;
        color: var(--gray-700);
        padding: 0;
        b {
            font-family: 'SourceSansPro-SemiBold', sans-serif;
            color: var(--gray-800);
        }
    }
`
